import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch,
  FormControlLabel, Snackbar, Alert, Chip, RadioGroup, Radio,
  FormControl, FormLabel, FormHelperText
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';

const getApiBaseUrl = () => {
  const environment = process.env.REACT_APP_FIREBASE_ENV || 'test';
  switch (environment) {
    case 'production':
      return 'https://adminapi-a2b5o5ydga-uc.a.run.app';
    case 'test':
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
    default:
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
  }
};

const getAuthToken = () => localStorage.getItem('admin_token') || '';

type DistributionType = 'code_only' | 'auto_first_purchase' | 'auto_all';
type ExpiryType = 'date' | 'days_from_acquisition' | 'none';

interface Coupon {
  id: string;
  code: string;
  type: string;
  discountPercent: number;
  maxDiscount: number;
  isForNewUsers: boolean;
  distributionType?: DistributionType;
  isActive: boolean;
  isAcquisitionActive: boolean;
  isUsageActive: boolean;
  expiryType?: ExpiryType;
  expiryDays?: number;
  description: string;
  expiresAt: any;
}

const getDistributionType = (coupon: Coupon): DistributionType => {
  if (coupon.distributionType) return coupon.distributionType;
  return coupon.isForNewUsers ? 'auto_first_purchase' : 'code_only';
};

const distributionLabel = (type: DistributionType): string => {
  switch (type) {
    case 'auto_first_purchase': return '自動配布（初回購入者のみ）';
    case 'auto_all': return '自動配布（全ユーザー）';
    case 'code_only': return 'コード入力のみ';
  }
};

const CouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCoupon, setEditCoupon] = useState<Coupon | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    discountPercent: 5,
    maxDiscount: 1000,
    distributionType: 'auto_first_purchase' as DistributionType,
    isAcquisitionActive: true,
    isUsageActive: true,
    expiryType: 'none' as ExpiryType,
    expiryDays: 7,
    description: '',
    expiresAt: '',
  });

  useEffect(() => { loadCoupons(); }, []);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/coupons`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCoupons(data);
      } else {
        console.error('Failed to load coupons:', res.status);
      }
    } catch (e) {
      console.error('Error loading coupons:', e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      const couponData: any = {
        code: formData.code,
        type: formData.type,
        discountPercent: formData.discountPercent,
        maxDiscount: formData.maxDiscount,
        distributionType: formData.distributionType,
        isForNewUsers: formData.distributionType === 'auto_first_purchase',
        isForAllUsers: formData.distributionType === 'auto_all',
        isAcquisitionActive: formData.isAcquisitionActive,
        isUsageActive: formData.isUsageActive,
        isActive: formData.isAcquisitionActive || formData.isUsageActive,
        expiryType: formData.expiryType,
        expiryDays: formData.expiryType === 'days_from_acquisition' ? formData.expiryDays : null,
        description: formData.description,
        expiresAt: formData.expiryType === 'date' ? (formData.expiresAt || null) : null,
      };

      if (editCoupon) {
        const res = await fetch(`${getApiBaseUrl()}/coupons/${editCoupon.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(couponData),
        });
        if (!res.ok) throw new Error(`Update failed: ${res.status}`);
        setSnackbar({ open: true, message: 'クーポンを更新しました', severity: 'success' });
      } else {
        const res = await fetch(`${getApiBaseUrl()}/coupons`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(couponData),
        });
        if (!res.ok) throw new Error(`Create failed: ${res.status}`);
        setSnackbar({ open: true, message: 'クーポンを作成しました', severity: 'success' });
      }

      setDialogOpen(false);
      setEditCoupon(null);
      loadCoupons();
    } catch (e) {
      console.error('Save error:', e);
      setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('このクーポンを削除しますか？')) return;
    try {
      const res = await fetch(`${getApiBaseUrl()}/coupons/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      setSnackbar({ open: true, message: 'クーポンを削除しました', severity: 'success' });
      loadCoupons();
    } catch (e) {
      console.error('Delete error:', e);
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    }
  };

  const resolveExpiryType = (coupon: Coupon): ExpiryType => {
    if (coupon.expiryType) return coupon.expiryType;
    if (coupon.expiresAt) return 'date';
    return 'none';
  };

  const openCreate = () => {
    setEditCoupon(null);
    setFormData({
      code: '', type: 'percentage', discountPercent: 5, maxDiscount: 1000,
      distributionType: 'auto_first_purchase',
      isAcquisitionActive: true, isUsageActive: true,
      expiryType: 'none', expiryDays: 7,
      description: '', expiresAt: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (coupon: Coupon) => {
    setEditCoupon(coupon);
    const legacyActive = coupon.isActive ?? true;
    setFormData({
      code: coupon.code,
      type: coupon.type,
      discountPercent: coupon.discountPercent,
      maxDiscount: coupon.maxDiscount,
      distributionType: getDistributionType(coupon),
      isAcquisitionActive: coupon.isAcquisitionActive ?? legacyActive,
      isUsageActive: coupon.isUsageActive ?? legacyActive,
      expiryType: resolveExpiryType(coupon),
      expiryDays: coupon.expiryDays ?? 7,
      description: coupon.description,
      expiresAt: coupon.expiresAt?.toDate?.()?.toISOString?.()?.split('T')[0] || coupon.expiresAt || '',
    });
    setDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">クーポン管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>新規作成</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>コード</TableCell>
              <TableCell>割引</TableCell>
              <TableCell>上限</TableCell>
              <TableCell>配布方法</TableCell>
              <TableCell>有効期限</TableCell>
              <TableCell>状態</TableCell>
              <TableCell>説明</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.map((coupon) => (
              <TableRow key={coupon.id}>
                <TableCell><strong>{coupon.code}</strong></TableCell>
                <TableCell>{coupon.discountPercent}%OFF</TableCell>
                <TableCell>¥{coupon.maxDiscount?.toLocaleString()}</TableCell>
                <TableCell>
                  <Chip
                    label={distributionLabel(getDistributionType(coupon))}
                    size="small"
                    color={getDistributionType(coupon) === 'code_only' ? 'secondary' : 'primary'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {(coupon.expiryType === 'days_from_acquisition')
                    ? `取得後${coupon.expiryDays ?? '?'}日間`
                    : coupon.expiresAt
                      ? (typeof coupon.expiresAt === 'string'
                          ? coupon.expiresAt
                          : coupon.expiresAt?._seconds
                            ? new Date(coupon.expiresAt._seconds * 1000).toLocaleDateString('ja-JP')
                            : '—')
                      : '無期限'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`取得: ${(coupon.isAcquisitionActive ?? coupon.isActive) ? 'ON' : 'OFF'}`}
                      color={(coupon.isAcquisitionActive ?? coupon.isActive) ? 'success' : 'default'}
                      size="small" variant="outlined"
                    />
                    <Chip
                      label={`使用: ${(coupon.isUsageActive ?? coupon.isActive) ? 'ON' : 'OFF'}`}
                      color={(coupon.isUsageActive ?? coupon.isActive) ? 'success' : 'default'}
                      size="small" variant="outlined"
                    />
                  </Box>
                </TableCell>
                <TableCell>{coupon.description}</TableCell>
                <TableCell>
                  <IconButton onClick={() => openEdit(coupon)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(coupon.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCoupon ? 'クーポン編集' : 'クーポン作成'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="クーポンコード" value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              helperText="ユーザーがアプリで入力するコード（例: WELCOME2026）" />
            <TextField label="割引率 (%)" type="number" value={formData.discountPercent}
              onChange={(e) => setFormData({ ...formData, discountPercent: Number(e.target.value) })} />
            <TextField label="割引上限 (円)" type="number" value={formData.maxDiscount}
              onChange={(e) => setFormData({ ...formData, maxDiscount: Number(e.target.value) })} />
            <TextField label="説明（アプリに表示）" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              helperText="空の場合「5%OFF (上限¥1000)」のように自動表示されます" />
            <FormControl component="fieldset" sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>有効期限</FormLabel>
              <RadioGroup
                value={formData.expiryType}
                onChange={(e) => setFormData({ ...formData, expiryType: e.target.value as ExpiryType })}
              >
                <FormControlLabel value="none" control={<Radio />} label="無期限" />
                <FormControlLabel value="date" control={<Radio />} label="日付指定" />
                <FormControlLabel value="days_from_acquisition" control={<Radio />} label="取得後○日間有効" />
              </RadioGroup>
              {formData.expiryType === 'date' && (
                <TextField label="有効期限日" type="date" value={formData.expiresAt}
                  InputLabelProps={{ shrink: true }} sx={{ mt: 1 }}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })} />
              )}
              {formData.expiryType === 'days_from_acquisition' && (
                <TextField label="取得後の有効日数" type="number" value={formData.expiryDays}
                  sx={{ mt: 1 }}
                  onChange={(e) => setFormData({ ...formData, expiryDays: Number(e.target.value) })}
                  helperText="ユーザーがクーポンを取得した時点から起算されます" />
              )}
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>配布方法</FormLabel>
              <RadioGroup
                value={formData.distributionType}
                onChange={(e) => setFormData({ ...formData, distributionType: e.target.value as DistributionType })}
              >
                <FormControlLabel
                  value="auto_first_purchase"
                  control={<Radio />}
                  label="自動配布（初回購入者のみ）"
                />
                <FormHelperText sx={{ ml: 4, mt: -0.5, mb: 1 }}>
                  まだ購入したことがないユーザーにログイン時自動で付与されます。
                </FormHelperText>
                <FormControlLabel
                  value="auto_all"
                  control={<Radio />}
                  label="自動配布（全ユーザー）"
                />
                <FormHelperText sx={{ ml: 4, mt: -0.5, mb: 1 }}>
                  ログインしている全ユーザーに自動で付与されます。購入履歴に関係なく配布。
                </FormHelperText>
                <FormControlLabel
                  value="code_only"
                  control={<Radio />}
                  label="コード入力限定"
                />
                <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                  クーポンコードを知っている人だけが取得できます。広告やSNS経由で配布する場合に使用。
                </FormHelperText>
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>状態管理</FormLabel>
              <FormControlLabel
                control={<Switch checked={formData.isAcquisitionActive}
                  onChange={(e) => setFormData({ ...formData, isAcquisitionActive: e.target.checked })} />}
                label="取得可能"
              />
              <FormHelperText sx={{ ml: 4, mt: -1 }}>
                OFFにすると新規取得（コード入力・自動配布）が停止されます。既に取得済みのユーザーには影響しません。
              </FormHelperText>
              <FormControlLabel sx={{ mt: 1 }}
                control={<Switch checked={formData.isUsageActive}
                  onChange={(e) => setFormData({ ...formData, isUsageActive: e.target.checked })} />}
                label="使用可能"
              />
              <FormHelperText sx={{ ml: 4, mt: -1 }}>
                OFFにすると既に取得済みのクーポンも含めて使用不可になります。
              </FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSave}>保存</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponsPage;
